import { throttle } from '@theme/utilities';
import { CartAddEvent } from './events.js';

/**
 * Product Scroll Add to Cart Component
 * Handles the sticky add to cart bar that appears on scroll
 */
class ProductScrollAddToCart {
  #currentProductId = null;
  #formSyncAbortController = null;
  #isFormSyncSetup = false;
  #reinitTimeout;

  constructor() {
    this.stickyAddToCart = document.getElementById('Product__add-to-cart');
    this.stickyQuantitySelector = this.stickyAddToCart?.querySelector('quantity-selector-component');
    this.mainQuantitySelector = document.querySelector('quantity-selector-component');
    
    if (!this.stickyAddToCart) return;
    
    this.init();
    this.#setupNavigationDetection();
  }

  init() {
    this.setupScrollListener();
    this.setupFormSync();
  }

  setupScrollListener() {
    // Throttle scroll events for performance
    const handleScroll = throttle(() => {
      this.updateStickyVisibility();
    }, 16); // ~60fps

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    this.updateStickyVisibility();
  }

  /**
   * Gets the current product ID from the page
   * @returns {string|null} The current product ID or null if not found
   */
  #getCurrentProductId() {
    // Try to get from sticky product-form-component first (most reliable)
    const stickyProductForm = this.stickyAddToCart?.querySelector('product-form-component[data-product-id]');
    if (stickyProductForm?.dataset?.productId) {
      return String(stickyProductForm.dataset.productId);
    }
    
    // Fallback: find any product-form-component on the page (excluding sticky)
    const mainProductForm = document.querySelector('product-form-component[data-product-id]:not([data-is-sticky="true"])');
    if (mainProductForm?.dataset?.productId) {
      return String(mainProductForm.dataset.productId);
    }
    
    // Fallback: extract from URL (this will be a handle, not numeric ID)
    const urlMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
    
    return null;
  }

  /**
   * Gets the main product form (not the sticky form) for the current product
   * @returns {HTMLFormElement|null} The main product form or null if not found
   */
  #getMainProductForm() {
    // Find all product forms on the page
    const allForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    // First, try to find form by product ID match
    const currentProductId = this.#getCurrentProductId();
    if (currentProductId) {
      for (const form of allForms) {
        // Skip forms inside the sticky add-to-cart wrapper
        if (form.closest('#Product__add-to-cart') || form.closest('.sticky-add-to-cart-wrapper')) {
          continue;
        }
        
        // Check if this form belongs to the current product
        const productFormComponent = form.closest('product-form-component[data-product-id]');
        const formProductId = productFormComponent?.dataset?.productId;
        
        // Match by string comparison (handles both numeric IDs and handles)
        if (formProductId && String(formProductId) === String(currentProductId)) {
          return form;
        }
      }
    }
    
    // Fallback: return first non-sticky form (most common case on product pages)
    for (const form of allForms) {
      if (!form.closest('#Product__add-to-cart') && !form.closest('.sticky-add-to-cart-wrapper')) {
        return form;
      }
    }
    
    return null;
  }

  updateStickyVisibility() {
    if (!this.stickyAddToCart) return;

    const stickyAddToCart = this.stickyAddToCart;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    
    // Find the main product form to determine when to show/hide sticky bar
    const mainProductForm = this.#getMainProductForm();
    let shouldShow = false;
    
    if (mainProductForm) {
      const formRect = mainProductForm.getBoundingClientRect();
      const formBottom = formRect.bottom;
      
      // Show sticky bar when main form is out of view (below viewport)
      // Add some buffer (100px) to ensure smooth transition
      shouldShow = formBottom < -100;
      
      // Hide sticky bar when scrolling back up and main form is about to come into view
      // Show main form when it's within 200px of viewport top
      if (shouldShow && formBottom > -200) {
        shouldShow = false;
      }
    } else {
      // Fallback: show when scrolled past 70% of viewport (higher threshold)
      // This ensures the sticky bar still appears even if we can't find the main form
      shouldShow = scrollTop > windowHeight * 0.7;
    }

    requestAnimationFrame(() => {
      stickyAddToCart.classList.toggle('is-visible', shouldShow);
    });
  }


  setupFormSync() {
    // Clean up any existing listeners
    if (this.#formSyncAbortController) {
      this.#formSyncAbortController.abort();
    }
    this.#formSyncAbortController = new AbortController();
    this.#isFormSyncSetup = false;

    // Find the sticky add-to-cart component and sync it with the main form
    const stickyAddToCartComponent = this.stickyAddToCart?.querySelector('add-to-cart-component');
    if (!stickyAddToCartComponent) return;

    // Handle form submission from sticky form
    const stickySubmitBtn = stickyAddToCartComponent.querySelector('button[type="submit"]');
    if (stickySubmitBtn && !this.#isFormSyncSetup) {
      this.#isFormSyncSetup = true;
      
      stickySubmitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Get the sticky form and its product-form-component
        const stickyForm = stickySubmitBtn.closest('form');
        const stickyProductFormComponent = stickyForm?.closest('product-form-component[data-product-id]');
        
        if (!stickyForm || !stickyProductFormComponent) {
          console.error('ProductScrollAddToCart: Sticky form or product-form-component not found');
          return;
        }
        
        // Get product ID from sticky form component
        const stickyProductId = stickyProductFormComponent.dataset.productId;
        if (!stickyProductId) {
          console.error('ProductScrollAddToCart: No product ID found on sticky form component');
          return;
        }
        
        // Validate that sticky form belongs to current product
        const currentProductId = this.#getCurrentProductId();
        if (currentProductId && String(stickyProductId) !== String(currentProductId)) {
          console.warn('ProductScrollAddToCart: Sticky form product ID mismatch. Current:', currentProductId, 'Sticky:', stickyProductId);
          // Still allow submission but log warning - product ID might be handle vs numeric
        }
        
        // Get the variant ID from the sticky form itself (it's kept in sync via variant update events)
        const stickyVariantInput = stickyForm.querySelector('input[name="id"]');
        let variantId = stickyVariantInput && stickyVariantInput instanceof HTMLInputElement ? stickyVariantInput.value : '';
        
        // Fallback: if sticky form doesn't have variant ID, try main form
        if (!variantId) {
          const mainProductForm = this.#getMainProductForm();
          if (mainProductForm) {
            const mainVariantInput = mainProductForm.querySelector('input[name="id"]');
            variantId = mainVariantInput && mainVariantInput instanceof HTMLInputElement ? mainVariantInput.value : '';
            
            // Validate main form belongs to same product
            const mainFormProductId = mainProductForm.closest('product-form-component[data-product-id]')?.dataset?.productId;
            if (mainFormProductId && String(mainFormProductId) !== String(stickyProductId)) {
              console.warn('ProductScrollAddToCart: Main form product ID mismatch with sticky form. Sticky:', stickyProductId, 'Main:', mainFormProductId);
              // Don't use main form variant ID if products don't match
              variantId = '';
            }
          }
        }
        
        if (!variantId) {
          console.error('ProductScrollAddToCart: No variant ID found in sticky or main form');
          return;
        }
        
        // Additional validation: ensure variant ID is numeric
        const variantIdNum = parseInt(variantId, 10);
        if (isNaN(variantIdNum) || variantIdNum <= 0) {
          console.error('ProductScrollAddToCart: Invalid variant ID:', variantId);
          return;
        }
        
        // Get the quantity from the sticky quantity selector
        const stickyQuantityInput = this.stickyQuantitySelector?.querySelector('input[name="quantity"]');
        const quantity = stickyQuantityInput && stickyQuantityInput instanceof HTMLInputElement ? parseInt(stickyQuantityInput.value || '1', 10) : 1;

        // Submit the form programmatically with the correct quantity
        this.submitStickyForm(variantId, quantity);
      }, { signal: this.#formSyncAbortController.signal });
    }
  }

  async submitStickyForm(variantId, quantity) {
    try {
      // Collect all cart-items-component section IDs
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      // Add cart-icon to sections to ensure cart bubble updates
      sectionIds.push('cart-icon');
      
      // Also add any header sections that might contain the cart icon
      const headerSections = document.querySelectorAll('[data-section-id*="header"]');
      headerSections.forEach((header) => {
        if (header instanceof HTMLElement && header.dataset.sectionId) {
          sectionIds.push(header.dataset.sectionId);
        }
      });

      // Add main product form section if it exists
      const mainProductForm = this.#getMainProductForm();
      if (mainProductForm && mainProductForm.closest('[data-section-id]')) {
        const sectionElement = mainProductForm.closest('[data-section-id]');
        if (sectionElement && sectionElement instanceof HTMLElement && sectionElement.dataset.sectionId) {
          sectionIds.push(sectionElement.dataset.sectionId);
        }
      }

      // Ensure we have unique section IDs
      const uniqueSectionIds = [...new Set(sectionIds)];

      const body = {
        id: variantId,
        quantity: quantity,
        properties: { _added_from: 'ProductPage' },
        sections: uniqueSectionIds.join(','),
        sections_url: window.location.pathname
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();
      const sections = result.sections || {};

      // Get product ID from the sticky product-form-component
      const stickyProductFormComponent = this.stickyAddToCart?.querySelector('product-form-component');
      const productId = stickyProductFormComponent?.dataset?.productId;

      // Dispatch CartAddEvent to trigger upsell drawer
      const cartAddEvent = new CartAddEvent(
        {}, // resource (empty object like in product-form.js)
        'sticky-add-to-cart', // sourceId
        {
          source: 'sticky-add-to-cart',
          productId: productId,
          variantId: variantId,
          sections: sections
        }
      );
      document.dispatchEvent(cartAddEvent);

      // Reset sticky quantity to 1 after adding to cart
      this.resetStickyQuantity();

      // Force refresh cart bubble to fetch the latest cart count
      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon && typeof cartIcon.forceRefreshCartCount === 'function') {
        setTimeout(() => {
          cartIcon.forceRefreshCartCount();
        }, 100);
      }

      // Show success feedback
      this.showSuccessFeedback();

    } catch (error) {
      console.error('Failed to add to cart:', error);
      this.showErrorFeedback();
    }
  }

  resetStickyQuantity() {
    // Reset the sticky quantity selector to 1 after adding to cart
    const stickyQuantityInput = this.stickyQuantitySelector?.querySelector('input[name="quantity"]');
    if (stickyQuantityInput && stickyQuantityInput instanceof HTMLInputElement) {
      requestAnimationFrame(() => {
        stickyQuantityInput.value = '1';
      });
    }
  }

  showSuccessFeedback() {
    const button = this.stickyAddToCart?.querySelector('button[type="submit"]');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const originalText = button.innerHTML;
    requestAnimationFrame(() => {
      button.innerHTML = `
        <span class="add-to-cart-text--added">
          <span class="svg-wrapper add-to-cart-icon--added">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 4L6 11L3 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span>Toegevoegd</span>
        </span>
      `;
      button.disabled = true;
    });

    setTimeout(() => {
      requestAnimationFrame(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      });
    }, 2000);
  }

  showErrorFeedback() {
    const button = this.stickyAddToCart?.querySelector('button[type="submit"]');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const originalText = button.innerHTML;
    requestAnimationFrame(() => {
      button.innerHTML = `
        <span class="add-to-cart-text">
          <span class="svg-wrapper add-to-cart-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span>Fout</span>
        </span>
      `;
    });

    setTimeout(() => {
      requestAnimationFrame(() => {
        button.innerHTML = originalText;
      });
    }, 2000);
  }

  /**
   * Sets up navigation detection to re-initialize form references when product page changes
   */
  #setupNavigationDetection() {
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', () => {
      this.#handlePageChange();
    });

    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.#handlePageChange();
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.#handlePageChange();
    };

    // Listen for DOM mutations that might indicate page content changes
    // This helps catch cases where content is updated via AJAX/morphing
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;
      
      for (const mutation of mutations) {
        // Check if product-form-component was added/removed
        if (mutation.type === 'childList') {
          for (const node of [...mutation.addedNodes, ...mutation.removedNodes]) {
            if (node instanceof Element && (
              node.matches('product-form-component') ||
              node.querySelector('product-form-component')
            )) {
              shouldReinit = true;
              break;
            }
          }
        }
        
        if (shouldReinit) break;
      }
      
      if (shouldReinit) {
        // Debounce to avoid excessive re-initialization
        clearTimeout(this.#reinitTimeout);
        this.#reinitTimeout = setTimeout(() => {
          this.#handlePageChange();
        }, 100);
      }
    });

    // Observe changes to the document body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handles page changes by updating product ID and re-initializing form sync
   */
  #handlePageChange() {
    const newProductId = this.#getCurrentProductId();
    
    // Only re-initialize if product ID actually changed
    if (newProductId && newProductId !== this.#currentProductId) {
      this.#currentProductId = newProductId;
      // Re-setup form sync to ensure we're using the correct form
      this.setupFormSync();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductScrollAddToCart();
  });
} else {
  new ProductScrollAddToCart();
}

export default ProductScrollAddToCart; 